
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Edit3, Save, X } from "lucide-react";

interface ExtendedUserProfile {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  avatar_url?: string;
  phone?: string;
  linkedin_url?: string;
  location_city?: string;
  location_state?: string;
  location_country?: string;
  timezone?: string;
  bio?: string;
  years_experience?: number;
  specializations?: string[];
  work_hours_start?: string;
  work_hours_end?: string;
  preferred_communication?: string;
}

interface ContactInfoSectionProps {
  userProfile: ExtendedUserProfile;
  onUpdate: (updates: Partial<ExtendedUserProfile>) => Promise<void>;
}

const timezones = [
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "Europe/London", "Europe/Paris", "Europe/Berlin", "Asia/Tokyo", "Asia/Shanghai",
  "Australia/Sydney", "UTC"
];

export const ContactInfoSection: React.FC<ContactInfoSectionProps> = ({
  userProfile,
  onUpdate,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    phone: userProfile.phone || "",
    location_city: userProfile.location_city || "",
    location_state: userProfile.location_state || "",
    location_country: userProfile.location_country || "",
    timezone: userProfile.timezone || "",
  });

  useEffect(() => {
    setFormData({
      phone: userProfile.phone || "",
      location_city: userProfile.location_city || "",
      location_state: userProfile.location_state || "",
      location_country: userProfile.location_country || "",
      timezone: userProfile.timezone || "",
    });
  }, [userProfile]);

  const handleSave = async () => {
    const updates: Partial<ExtendedUserProfile> = {};
    
    Object.keys(formData).forEach(key => {
      const typedKey = key as keyof typeof formData;
      if (formData[typedKey] !== (userProfile[typedKey] || "")) {
        updates[typedKey] = formData[typedKey] || undefined;
      }
    });

    if (Object.keys(updates).length > 0) {
      await onUpdate(updates);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData({
      phone: userProfile.phone || "",
      location_city: userProfile.location_city || "",
      location_state: userProfile.location_state || "",
      location_country: userProfile.location_country || "",
      timezone: userProfile.timezone || "",
    });
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="phone">Phone Number</Label>
          <Input 
            id="phone" 
            value={formData.phone} 
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            placeholder="+1 (555) 123-4567"
            type="tel"
          />
        </div>
        
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="city">City</Label>
            <Input 
              id="city" 
              value={formData.location_city} 
              onChange={(e) => setFormData({ ...formData, location_city: e.target.value })}
              placeholder="San Francisco"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="state">State/Province</Label>
            <Input 
              id="state" 
              value={formData.location_state} 
              onChange={(e) => setFormData({ ...formData, location_state: e.target.value })}
              placeholder="California"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="country">Country</Label>
            <Input 
              id="country" 
              value={formData.location_country} 
              onChange={(e) => setFormData({ ...formData, location_country: e.target.value })}
              placeholder="United States"
            />
          </div>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="timezone">Timezone</Label>
          <Select value={formData.timezone} onValueChange={(value) => setFormData({ ...formData, timezone: value })}>
            <SelectTrigger>
              <SelectValue placeholder="Select your timezone" />
            </SelectTrigger>
            <SelectContent>
              {timezones.map((tz) => (
                <SelectItem key={tz} value={tz}>
                  {tz}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex gap-2 pt-2">
          <Button onClick={handleSave}>
            <Save className="h-4 w-4 mr-2" />
            Save Changes
          </Button>
          <Button variant="outline" onClick={handleCancel}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  const location = [userProfile.location_city, userProfile.location_state, userProfile.location_country]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Contact & Location</h3>
        <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
          <Edit3 className="h-4 w-4 mr-2" />
          Edit
        </Button>
      </div>
      
      <div className="grid grid-cols-2 gap-6">
        <div>
          <Label className="block text-muted-foreground text-sm mb-1">Phone Number</Label>
          <p className="text-sm">{userProfile.phone || "Not set"}</p>
        </div>
        <div>
          <Label className="block text-muted-foreground text-sm mb-1">Timezone</Label>
          <p className="text-sm">{userProfile.timezone || "Not set"}</p>
        </div>
      </div>
      
      <div>
        <Label className="block text-muted-foreground text-sm mb-1">Location</Label>
        <p className="text-sm">{location || "Not set"}</p>
      </div>
    </div>
  );
};
